import { serviceSlice } from "../../app/serviceSlice";
import { CRFService } from "./nebula.port";
import { Crf, CrfVisit, ICRFEEditRequest, ICRFFinalRequest, Laterality, CrfReadingValue } from "./nebula.service";

export const mergeCollections = (...arrays: Record<any, any>[]) => {
    const merged = {};

    // @ts-ignore
    arrays.forEach((data) => data.forEach((o) => Object.assign((merged[o.fieldName] ??= {}), o)));

    return Object.values(merged);
};

const extendedApiSlice = serviceSlice.enhanceEndpoints({ addTagTypes: ["CRF"] }).injectEndpoints({
    endpoints: (builder) => ({
        getPatientCrf: builder.query<Crf, string>({
            query: (patientId) => () => CRFService.getPatientCrf(patientId),
            providesTags: ["CRF"],
        }),
        removeCrfFields: builder.mutation<
            void,
            { patientId: string; visitId: string; laterality: string; fieldsToRemove: string[]; email: string }
        >({
            query:
                ({ patientId, visitId, laterality, fieldsToRemove }) =>
                () =>
                    CRFService.removeCrfValues(patientId, visitId, laterality, fieldsToRemove),
            async onQueryStarted(
                { patientId, fieldsToRemove, visitId, laterality, email },
                { dispatch, queryFulfilled }
            ) {
                const patchResult = dispatch(
                    extendedApiSlice.util.updateQueryData("getPatientCrf", patientId, (draft) => {
                        draft.visits.forEach((visit: CrfVisit) => {
                            if (visit.uuid === visitId) {
                                visit.data.forEach((crfReading) => {
                                    if (crfReading.laterality === laterality && crfReading.user.email === email) {
                                        crfReading.values = crfReading.values.filter(({ uuid }) => {
                                            return !fieldsToRemove.includes(uuid);
                                        });
                                    }
                                });
                            }
                        });
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        editCRF: builder.mutation<void, { patientId: string; crfUpdate: ICRFEEditRequest & { email: string } }>({
            query:
                ({ patientId, crfUpdate }) =>
                () => {
                    const { email, ...rest } = crfUpdate;
                    return CRFService.editCRF(patientId, rest);
                },

            async onQueryStarted({ patientId, crfUpdate }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    extendedApiSlice.util.updateQueryData("getPatientCrf", patientId, (draft) => {
                        draft.visits.forEach((visit: CrfVisit) => {
                            if (visit.uuid === crfUpdate.visitId) {
                                visit.data.forEach((crfReading) => {
                                    if (
                                        crfReading.laterality === crfUpdate.laterality &&
                                        crfReading.user.email === crfUpdate.email
                                    ) {
                                        // update values
                                        if (crfUpdate.data) {
                                            const updatedValues = mergeCollections(crfReading.values, crfUpdate.data);
                                            // @ts-ignore
                                            crfReading.values = updatedValues;
                                        }
                                        // update notes
                                        if (crfUpdate.notes !== undefined) {
                                            crfReading.notes = crfUpdate.notes;
                                        }
                                    }
                                });
                            }
                        });
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        setFinal: builder.mutation<void, { patientId: string; finalRequest: ICRFFinalRequest }>({
            query:
                ({ patientId, finalRequest }) =>
                () =>
                    CRFService.setFinal(patientId, finalRequest),

            async onQueryStarted({ patientId, finalRequest }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    extendedApiSlice.util.updateQueryData("getPatientCrf", patientId, (draft) => {
                        draft.visits.forEach((visit: CrfVisit) => {
                            if (visit.uuid === finalRequest.visitId) {
                                // Update the target reading
                                visit.data.forEach((reading) => {
                                    if (
                                        reading.laterality === finalRequest.laterality &&
                                        reading.user.uuid === finalRequest.userId
                                    ) {
                                        reading.final = true;
                                    }
                                });
                            }
                        });
                    })
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        unsetFinal: builder.mutation<void, { patientId: string; finalRequest: ICRFFinalRequest }>({
            query:
                ({ patientId, finalRequest }) =>
                () =>
                    CRFService.unsetFinal(patientId, finalRequest),

            async onQueryStarted({ patientId, finalRequest }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    extendedApiSlice.util.updateQueryData("getPatientCrf", patientId, (draft) => {
                        draft.visits.forEach((visit: CrfVisit) => {
                            if (visit.uuid === finalRequest.visitId) {
                                visit.data.forEach((reading) => {
                                    if (
                                        reading.laterality === finalRequest.laterality &&
                                        reading.user.uuid === finalRequest.userId
                                    ) {
                                        reading.final = false;
                                    }
                                });
                            }
                        });
                    })
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        adjudicateReadings: builder.mutation<
            void,
            { patientId: string; visitId: string; laterality: Laterality; data: CrfReadingValue[]; notes?: string }
        >({
            query:
                ({ patientId, ...request }) =>
                () =>
                    CRFService.adjudicateReadings(patientId, request),
            invalidatesTags: ["CRF"],
        }),
    }),
});

export const {
    useEditCRFMutation,
    useGetPatientCrfQuery,
    useRemoveCrfFieldsMutation,
    useSetFinalMutation,
    useUnsetFinalMutation,
    useAdjudicateReadingsMutation,
} = extendedApiSlice;
